/**
 * The A4 packing-slip document body (§12.3) as a standalone component, so it can
 * be rendered both on its own (PackingSlip route) and repeated — one per order —
 * for a whole batch in a single print run (BatchPackingSlips route).
 */
import type { PackingSlipData } from "../../lib/api.js";
import { Barcode } from "../../components/ui/index.js";
import { Thumb } from "../../components/ui/Thumb.js";
import { AddressBlock, DocBrand, fmtGrams, fmtDateTime } from "./docBits.js";

export function PackingSlipDoc({ data }: { data: PackingSlipData }) {
  return (
    <article className="doc" aria-label={`Packing slip for ${data.order.name}`}>
      <header className="doc-head">
        <DocBrand subtitle="Packing slip" />
        <div className="doc-head__right">
          <span className="doc-order">{data.order.name}</span>
          <Barcode value={data.barcodeValue} height={42} className="doc-barcode" />
          <span className="doc-meta">{fmtDateTime(data.order.createdAtShopify)}</span>
          {data.customerEmail && <span className="doc-meta">{data.customerEmail}</span>}
        </div>
      </header>

      <div className="doc-strip">
        <div className="doc-strip__item">
          <span className="doc-strip__label">Delivery method</span>
          <span className="doc-strip__value">{data.deliveryMethod}</span>
        </div>
        <div className="doc-strip__item">
          <span className="doc-strip__label">Payment method</span>
          <span className="doc-strip__value">{data.paymentMethod}</span>
        </div>
      </div>

      <div className="doc-parties">
        <AddressBlock label="Ship to" addr={data.shipTo} />
        <AddressBlock label="Bill to" addr={data.billTo} fallback="Same as delivery address" />
        <AddressBlock label="Warehouse — from" addr={data.warehouse} />
      </div>

      <table className="doc-table" aria-label="Order items">
        <thead>
          <tr>
            <th style={{ width: "48px" }}></th>
            <th>Item</th>
            <th>SKU</th>
            <th className="num" style={{ width: "70px" }}>
              Qty
            </th>
          </tr>
        </thead>
        <tbody>
          {data.lines.map((l, i) => (
            <tr key={`${l.sku ?? l.title}-${i}`}>
              <td>
                <Thumb src={l.imageUrl} alt={l.title} size={38} />
              </td>
              <td>
                <div className="doc-cell-title">{l.title}</div>
                <div className="doc-cell-sub">
                  {l.variantTitle ? `${l.variantTitle} · ` : ""}
                  {fmtGrams(l.unitWeightG)} each
                </div>
              </td>
              <td className="doc-sku">{l.sku ?? "—"}</td>
              <td className="num">
                <span className="doc-qty-pill">{l.quantity}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="doc-totals">
        <div>
          <div className="doc-total__label">Total weight</div>
          <div className="doc-total__value">{fmtGrams(data.totalWeightG)}</div>
        </div>
        <div>
          <div className="doc-total__label">Total items</div>
          <div className="doc-total__value">{data.totalItems}</div>
        </div>
      </div>

      <footer className="doc-foot">
        <span>{data.org.website}</span>
        <span>{data.org.email}</span>
        <span>{data.org.phone}</span>
      </footer>
    </article>
  );
}
